import { useEffect, useRef, useState } from 'react'
import { Text, XStack, YStack } from 'tamagui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useApp, type Localized } from '../../../i18n'
import { api, type ApiFieldError } from '../../../api/client'
import { tone } from '../../../theme/tones'
import { Button, DateField, FormInput, FormTextArea, Icon, IconButton, OptionSelect } from '../../../ui'
import { pickFileWeb } from '../../../lib/files'
import type { ItWorksDirection, ItWorksInput, ItWorksPerson, RequestCard } from '../../../lib/types'
import { useMe } from '../../users/useUsers'
import { errorText } from '../errorText'
import { PeoplePicker } from '../forms/ContractForm'
import { PersonPickField } from '../forms/PersonPickField'
import { Field, FormActions, dateCol, isWeb } from '../forms/shared'
import {
  IT_WORKS_CURRENCIES, MSG, canAddReport, contractorCurrency, emptyItWorksForm, filePickError, freshUploadIds, fromItWorksPayload,
  itWorksMissing, newRowId, periodLabel, recentPeriods, serverText, switchDirection, toItWorksInput, withDirectionDefaults,
  type ItWorksFile, type ItWorksFormState, type ItWorksLineRow, type ItWorksReportRow,
} from './itWorksRules'
import { reworkNotes, type ReworkNote } from './itWorksCard'

/**
 * Заявка на приёмку IT-работ (ТЗ-2026-АКТ001): шапка, счёт, отчёты (Грузия — сводный и приложения,
 * Баку — отчёт от каждого автора), строки модулей с согласующими, финальный утверждающий, получатели.
 *
 * Три режима: новая заявка; открытый черновик (`card`) — форма из его payload, сохранение в тот же
 * черновик; доработка (`mode="revise"`) — направление заперто, главная кнопка отдаёт форму в
 * `onRevise`. Файлы грузятся сразу при выборе (`uploadEdoDoc`): черновик хранит только uploadId.
 * Правила подачи зеркалит itWorksRules.ts; 400 сервера подсвечивает поля по `errors[].field`.
 */
export type ItWorksFormProps = {
  onDone: () => void
  /** «Отмена»; по умолчанию onDone. Форма внутри карточки по «Отмене» сворачивается, а не закрывает карточку. */
  onCancel?: () => void
} & (
  | { mode?: 'create'; card?: RequestCard; onRevise?: never }
  | {
      mode: 'revise'
      card: RequestCard
      /**
       * Форма и uploadId файлов, загруженных в этой правке и оставшихся в форме (сервер отбивает
       * лишние). Верните промис (`mutateAsync`) — форма покажет ошибку и закроется (onDone) только
       * после успешного ответа. Обязателен: без него форма закрылась бы, ничего не отправив.
       */
      onRevise: (input: ItWorksInput, newUploadIds: string[]) => unknown
    }
)

const DIRECTIONS: { value: ItWorksDirection; label: Localized }[] = [
  { value: 'GEORGIA', label: { ru: 'Грузия', en: 'Georgia', az: 'Gürcüstan' } },
  { value: 'BAKU', label: { ru: 'Баку', en: 'Baku', az: 'Bakı' } },
]
const OTHER = '__other__'
const DATE_MIN = '2000-01-01'
const FILE_UPLOADED: Localized = { ru: 'Файл загружен', en: 'File uploaded', az: 'Fayl yüklənib' }
const WEB_ONLY: Localized = {
  ru: 'Прикрепление файлов доступно в веб-версии портала',
  en: 'File upload is available in the web version',
  az: 'Faylların əlavə edilməsi portalın veb versiyasında mümkündür',
}

export function ItWorksForm(props: ItWorksFormProps) {
  const { onDone, card } = props
  const { t, lang } = useApp()
  const qc = useQueryClient()
  const meId = useMe().data?.id
  const revise = props.mode === 'revise'
  const [f, setF] = useState<ItWorksFormState>(() =>
    card
      ? fromItWorksPayload(card.payload, {
          attachments: card.attachments,
          people: [card.itWorks?.finalApprover, ...(card.itWorks?.recipients ?? []), ...(card.tasks ?? []).map((x) => x.assignee)],
        })
      : emptyItWorksForm('GEORGIA', new Date()),
  )
  const up = (patch: Partial<ItWorksFormState>) => setF((prev) => ({ ...prev, ...patch }))
  const setReports = (fn: (rs: ItWorksReportRow[]) => ItWorksReportRow[]) => setF((prev) => ({ ...prev, reports: fn(prev.reports) }))
  const setLine = (id: string, patch: Partial<ItWorksLineRow>) =>
    setF((prev) => ({ ...prev, modules: prev.modules.map((l) => (l.id === id ? { ...l, ...patch } : l)) }))

  const options = useQuery({
    queryKey: ['it-works-options', f.direction],
    queryFn: () => api.itWorksOptions(f.direction),
    staleTime: 60_000,
  })
  const o = options.data?.direction === f.direction ? options.data : undefined

  // Умолчания направления — один раз на направление, когда пришли его справочники. Черновик и
  // доработка приходят со своими значениями: для их направления умолчания уже «применены».
  const [defaultsFor, setDefaultsFor] = useState<ItWorksDirection | null>(card ? f.direction : null)
  useEffect(() => {
    if (!o || !meId || defaultsFor === o.direction) return
    setF((prev) => (prev.direction === o.direction ? withDirectionDefaults(prev, o, meId) : prev))
    setDefaultsFor(o.direction)
  }, [o, meId, defaultsFor])

  const [pendingDir, setPendingDir] = useState<ItWorksDirection | null>(null)
  const changeDirection = (d: ItWorksDirection) => {
    if (d === f.direction) return
    if (f.reports.some((r) => r.file)) setPendingDir(d)
    else setF((prev) => switchDirection(prev, d))
  }

  // Загрузок в полёте: пока файл грузится, его ещё нет в форме — сохранять рано.
  const [uploading, setUploading] = useState(0)
  const onBusy = (d: number) => setUploading((n) => n + d)

  // Ошибки последнего ответа 400 по полям — подсветка до следующей попытки.
  const [fieldErrs, setFieldErrs] = useState<ApiFieldError[]>([])
  const errLine = (field: string) => {
    const m = fieldErrs.find((e) => e.field === field)?.message
    return m ? <Text fontSize={12} color={tone('red').text}>{t(serverText(m))}</Text> : null
  }
  const onError = (e: Error) => setFieldErrs((e as { errors?: ApiFieldError[] }).errors ?? [])
  // Пути ошибок позиционные (`modules[1]…`): строку добавили или убрали — индексы уже чужие.
  const rowIds = [...f.reports, ...f.modules].map((r) => r.id).join()
  useEffect(() => setFieldErrs([]), [rowIds])

  // Второй клик раньше, чем isPending дойдёт до кнопки, видел бы draftId === null и создал бы
  // второй черновик: защёлка на время запроса.
  const inFlight = useRef(false)
  const once = (go: () => void) => {
    if (inFlight.current) return
    inFlight.current = true
    go()
  }
  const onSettled = () => { inFlight.current = false }

  // id черновика: появляется при первом сохранении и остаётся после 400 на отправке — повтор
  // сохраняет тот же черновик, а не создаёт второй.
  const draftId = useRef<string | null>(revise ? null : (card?.id ?? null))
  const [saved, setSaved] = useState(false)
  const save = useMutation({
    mutationFn: async (submit: boolean) => {
      const input = toItWorksInput(f)
      if (draftId.current) await api.saveItWorks(draftId.current, input)
      else draftId.current = (await api.createItWorksDraft(input)).id
      if (submit) await api.submitItWorks(draftId.current)
    },
    onMutate: () => { setFieldErrs([]); setSaved(false) },
    onSuccess: (_, submit) => {
      if (submit) onDone()
      else setSaved(true)
    },
    onError,
    // И после сбоя: черновик мог успеть создаться/сохраниться до 400 на отправке — списки и карточка его видят.
    onSettled: () => {
      onSettled()
      qc.invalidateQueries({ queryKey: ['edo-requests'] })
      if (draftId.current) qc.invalidateQueries({ queryKey: ['edo-request', draftId.current] })
    },
  })
  const resubmit = useMutation({
    mutationFn: async () => (props.mode === 'revise' ? props.onRevise(toItWorksInput(f), freshUploadIds(f)) : undefined),
    onMutate: () => setFieldErrs([]),
    onSuccess: () => onDone(),
    onError,
    onSettled,
  })
  const m = revise ? resubmit : save

  const missing = [...new Set(itWorksMissing(f, o, meId).map((x) => t(x)))]
  const ok = missing.length === 0 && !!o && uploading === 0
  const submit = () => {
    if (!ok || m.isPending) return
    once(() => (revise ? resubmit.mutate() : save.mutate(true)))
  }
  // DTO принимает не больше 20 отчётов: новые строки отчётов — только пока есть место.
  const addReport = canAddReport(f)
  const capHint = <Text fontSize={12} color="$muted">{t(MSG.files)}</Text>

  const routeIds = [meId, f.finalApproverId, ...f.modules.map((l) => l.approver?.id)].filter((x): x is string => !!x)
  const summary = f.reports.find((r) => r.kind === 'summary')
  const known = new Map(
    [...(o?.modules.map((x) => x.defaultApprover) ?? []), ...(o?.finalApprovers ?? []), ...(o?.recipients ?? []), ...f.recipients]
      .filter((p): p is ItWorksPerson => !!p?.fullName)
      .map((p) => [p.id, p.fullName]),
  )
  const periods = recentPeriods(new Date())
  if (f.period && !periods.includes(f.period)) periods.push(f.period)
  // Доработка: за что вернули — под строкой модуля и (возврат с финала) над формой; лист закрывать не нужно.
  const notes = revise && card ? reworkNotes(card) : null
  const reworkLine = (n: ReworkNote | null | undefined, label: Localized) =>
    n ? (
      <Text fontSize={12} color={tone('red').text} lineHeight={17}>
        {t(label)}{n.by ? ` — ${n.by}` : ''}: «{n.comment}»
      </Text>
    ) : null
  const reportLabel = (r: ItWorksReportRow, i: number) =>
    `${i + 1}. ${o?.authors.find((a) => a.id === r.authorId)?.name ?? r.file?.name ?? t({ ru: 'Отчёт', en: 'Report', az: 'Hesabat' })}`

  const fileSlot = (file: ItWorksFile | null, onChange: (x: ItWorksFile | null) => void, kind: 'report' | 'invoice', addLabel: Localized) => (
    <FileSlot file={file} onChange={onChange} kind={kind} addLabel={addLabel} onBusy={onBusy} />
  )

  return (
    <YStack gap={14}>
      {options.isError && <Text fontSize={12} color={tone('red').text}>{t(errorText(options.error))}</Text>}
      {reworkLine(notes?.final, { ru: 'Возврат с финального утверждения', en: 'Returned from final approval', az: 'Yekun təsdiqdən qaytarılıb' })}

      <Field label={t({ ru: 'Направление', en: 'Direction', az: 'İstiqamət' })} required>
        {revise ? (
          <XStack alignItems="center" gap={8}>
            <Text fontSize={13} color="$color">{t(DIRECTIONS.find((d) => d.value === f.direction)!.label)}</Text>
            <Icon name="lock" size={14} color="$muted" />
          </XStack>
        ) : (
          <OptionSelect
            options={DIRECTIONS.map((d) => ({ value: d.value, label: t(d.label) }))}
            value={[f.direction]}
            onChange={(next) => changeDirection(next[0] as ItWorksDirection)}
          />
        )}
        <Text fontSize={12} color="$muted">
          {t({
            ru: 'От направления зависят плательщик, валюта и вид отчётов. На доработке его не меняют.',
            en: 'The direction defines the payer, currency and report type. It cannot change on rework.',
            az: 'İstiqamətdən ödəyici, valyuta və hesabat növü asılıdır. Yenidən işləmədə dəyişdirilmir.',
          })}
        </Text>
      </Field>
      {pendingDir && (
        <YStack gap={8} padding={10} borderRadius={7} borderWidth={1} borderColor="$borderColor" backgroundColor="$bgLayout">
          <Text fontSize={13} color="$color">
            {t({ ru: 'Прикреплённые отчёты будут сброшены', en: 'Attached reports will be cleared', az: 'Əlavə edilmiş hesabatlar silinəcək' })}
          </Text>
          <XStack gap={8} flexWrap="wrap">
            <Button size="sm" onPress={() => setPendingDir(null)}>{t({ ru: 'Отмена', en: 'Cancel', az: 'Ləğv et' })}</Button>
            <Button size="sm" variant="primary" onPress={() => { setF((prev) => switchDirection(prev, pendingDir)); setPendingDir(null) }}>
              {t({ ru: 'Сменить направление', en: 'Change direction', az: 'İstiqaməti dəyiş' })}
            </Button>
          </XStack>
        </YStack>
      )}

      <Field label={t({ ru: 'Подрядчик', en: 'Contractor', az: 'Podratçı' })} required>
        <OptionSelect
          list
          options={(o?.contractors ?? []).map((c) => ({ value: c.id, label: c.taxId ? `${c.name} · ${c.taxId}` : c.name }))}
          value={f.contractorId ? [f.contractorId] : []}
          onChange={(next) => {
            const c = o?.contractors.find((x) => x.id === next[0])
            up({ contractorId: next[0], currency: contractorCurrency(c?.currency) ?? f.currency })
          }}
          placeholder={t({ ru: 'Выберите подрядчика', en: 'Select a contractor', az: 'Podratçını seçin' })}
          highlight={!f.contractorId}
        />
        {errLine('contractorId')}
      </Field>

      <XStack gap={12} flexWrap="wrap">
        <YStack gap={6}>
          <Field label={t({ ru: 'Отчётный период', en: 'Reporting period', az: 'Hesabat dövrü' })} required>
            <OptionSelect list options={periods.map((p) => ({ value: p, label: periodLabel(p, lang) }))}
              value={f.period ? [f.period] : []} onChange={(next) => up({ period: next[0] })} highlight={!f.period} />
            {errLine('period')}
          </Field>
        </YStack>
        <YStack {...dateCol} gap={6}>
          <Field label={t({ ru: 'С (необязательно)', en: 'From (optional)', az: 'Başlanğıc (məcburi deyil)' })}>
            <DateField value={f.periodFrom} onChange={(v) => up({ periodFrom: v })} min={DATE_MIN} />
          </Field>
        </YStack>
        <YStack {...dateCol} gap={6}>
          <Field label={t({ ru: 'По (необязательно)', en: 'To (optional)', az: 'Bitmə (məcburi deyil)' })}>
            <DateField value={f.periodTo} onChange={(v) => up({ periodTo: v })} min={f.periodFrom || DATE_MIN} align="right" />
            {errLine('periodTo')}
          </Field>
        </YStack>
      </XStack>

      <XStack gap={12} flexWrap="wrap">
        <YStack flex={1} minWidth={160}>
          <Field label={t({ ru: 'Сумма к оплате', en: 'Amount to pay', az: 'Ödəniləcək məbləğ' })} required>
            {/* Два знака после запятой — больше DTO не примет. */}
            <FormInput value={f.amount} keyboardType="decimal-pad" placeholder="0.00"
              onChangeText={(v: string) => { if (/^\d*([.,]\d{0,2})?$/.test(v)) up({ amount: v }) }} />
            {errLine('amount')}
          </Field>
        </YStack>
        <YStack>
          <Field label={t({ ru: 'Валюта', en: 'Currency', az: 'Valyuta' })} required>
            <OptionSelect options={IT_WORKS_CURRENCIES} value={f.currency ? [f.currency] : []}
              onChange={(next) => up({ currency: contractorCurrency(next[0]) ?? '' })} />
            {errLine('currency')}
          </Field>
        </YStack>
      </XStack>

      <Field label={t({ ru: 'Счёт подрядчика (PDF)', en: 'Contractor invoice (PDF)', az: 'Podratçının hesabı (PDF)' })}>
        {fileSlot(f.invoice, (x) => up({ invoice: x }), 'invoice', { ru: 'Прикрепить счёт', en: 'Attach the invoice', az: 'Hesabı əlavə et' })}
        {errLine('invoiceUploadId')}
      </Field>

      {/* ── Отчёты ── */}
      {f.direction === 'GEORGIA' ? (
        <>
          <Field label={t({ ru: 'Сводный отчёт (PDF, DOCX, XLSX)', en: 'Summary report (PDF, DOCX, XLSX)', az: 'Yekun hesabat (PDF, DOCX, XLSX)' })} required>
            {summary || addReport ? fileSlot(summary?.file ?? null, (x) => setReports((rs) =>
              !x ? rs.filter((r) => r.kind !== 'summary')
                : rs.some((r) => r.kind === 'summary') ? rs.map((r) => (r.kind === 'summary' ? { ...r, file: x } : r))
                  : [...rs, { id: newRowId(), kind: 'summary', file: x, authorId: '', note: '' }],
            ), 'report', { ru: 'Прикрепить сводный отчёт', en: 'Attach the summary report', az: 'Yekun hesabatı əlavə et' }) : capHint}
            {summary && errLine(`reports[${f.reports.indexOf(summary)}].uploadId`)}
          </Field>
          <Field label={t({ ru: 'Приложения', en: 'Attachments', az: 'Əlavələr' })}>
            <YStack gap={8}>
              {f.reports.map((r, i) => r.kind === 'attachment' && (
                <YStack key={r.id} gap={4}>
                  {fileSlot(r.file, (x) => setReports((rs) => (x ? rs.map((y) => (y.id === r.id ? { ...y, file: x } : y)) : rs.filter((y) => y.id !== r.id))),
                    'report', { ru: 'Прикрепить файл', en: 'Attach a file', az: 'Fayl əlavə et' })}
                  {errLine(`reports[${i}].uploadId`)}
                </YStack>
              ))}
              {addReport
                ? fileSlot(null, (x) => x && setReports((rs) => [...rs, { id: newRowId(), kind: 'attachment', file: x, authorId: '', note: '' }]),
                  'report', { ru: 'Добавить приложение', en: 'Add an attachment', az: 'Əlavə əlavə et' })
                : capHint}
            </YStack>
          </Field>
        </>
      ) : (
        <Field label={t({ ru: 'Отчёты (PDF, DOCX, XLSX)', en: 'Reports (PDF, DOCX, XLSX)', az: 'Hesabatlar (PDF, DOCX, XLSX)' })} required>
          <YStack gap={10}>
            {f.reports.map((r, i) => (
              <YStack key={r.id} gap={8} padding={10} borderWidth={1} borderColor="$borderColor" borderRadius={8}>
                <RowHead n={i + 1} removeLabel={{ ru: 'Убрать отчёт', en: 'Remove report', az: 'Hesabatı sil' }}
                  onRemove={() => setReports((rs) => rs.filter((y) => y.id !== r.id))} />
                <OptionSelect list options={(o?.authors ?? []).map((a) => ({ value: a.id, label: a.name }))}
                  value={r.authorId ? [r.authorId] : []}
                  onChange={(next) => setReports((rs) => rs.map((y) => (y.id === r.id ? { ...y, authorId: next[0] } : y)))}
                  placeholder={t({ ru: 'Автор отчёта', en: 'Report author', az: 'Hesabatın müəllifi' })} />
                {errLine(`reports[${i}].authorId`)}
                {fileSlot(r.file, (x) => setReports((rs) => rs.map((y) => (y.id === r.id ? { ...y, file: x } : y))),
                  'report', { ru: 'Прикрепить отчёт', en: 'Attach the report', az: 'Hesabatı əlavə et' })}
                {errLine(`reports[${i}].uploadId`)}
                <FormInput value={r.note} maxLength={500}
                  onChangeText={(v: string) => setReports((rs) => rs.map((y) => (y.id === r.id ? { ...y, note: v } : y)))}
                  placeholder={t({ ru: 'Описание (необязательно)', en: 'Description (optional)', az: 'Təsvir (məcburi deyil)' })} />
              </YStack>
            ))}
            {addReport ? (
              <AddLink label={{ ru: 'Добавить отчёт', en: 'Add a report', az: 'Hesabat əlavə et' }}
                onPress={() => setReports((rs) => [...rs, { id: newRowId(), kind: 'report', file: null, authorId: '', note: '' }])} />
            ) : capHint}
          </YStack>
        </Field>
      )}
      {errLine('reports')}
      {errLine('files')}

      {/* ── Модули ── */}
      <Field label={t({ ru: 'Модули', en: 'Modules', az: 'Modullar' })} required>
        <YStack gap={10}>
          {f.modules.map((l, i) => (
            <YStack key={l.id} gap={8} padding={10} borderWidth={1} borderColor="$borderColor" borderRadius={8}>
              <RowHead n={i + 1} removeLabel={{ ru: 'Убрать модуль', en: 'Remove module', az: 'Modulu sil' }}
                onRemove={() => setF((prev) => ({ ...prev, modules: prev.modules.filter((y) => y.id !== l.id) }))} />
              {reworkLine(notes?.lines.get(l.id), { ru: 'На доработку', en: 'Rework', az: 'Düzəlişə' })}
              <OptionSelect list
                options={[
                  ...(o?.modules ?? []).map((x) => ({ value: x.id, label: x.name })),
                  { value: OTHER, label: t({ ru: 'Другое', en: 'Other', az: 'Digər' }) },
                ]}
                value={l.moduleId === null ? [OTHER] : l.moduleId ? [l.moduleId] : []}
                onChange={(next) => {
                  if (next[0] === OTHER) return setLine(l.id, { moduleId: null })
                  const def = o?.modules.find((x) => x.id === next[0])?.defaultApprover
                  setLine(l.id, { moduleId: next[0], ...(def && def.id !== meId ? { approver: def } : {}) })
                }}
                placeholder={t({ ru: 'Модуль', en: 'Module', az: 'Modul' })} highlight={l.moduleId === undefined} />
              {errLine(`modules[${i}].moduleId`)}
              {l.moduleId === null && (
                <>
                  <FormInput value={l.otherName} maxLength={100} onChangeText={(v: string) => setLine(l.id, { otherName: v })}
                    placeholder={t({ ru: 'Название модуля', en: 'Module name', az: 'Modulun adı' })} />
                  {errLine(`modules[${i}].otherName`)}
                </>
              )}
              {f.direction === 'BAKU' && (
                <>
                  <OptionSelect list options={f.reports.map((r, j) => ({ value: r.id, label: reportLabel(r, j) }))}
                    value={l.reportId ? [l.reportId] : []} onChange={(next) => setLine(l.id, { reportId: next[0] })}
                    placeholder={t({ ru: 'Отчёт строки', en: 'Line report', az: 'Sətrin hesabatı' })} highlight={!l.reportId} />
                  {errLine(`modules[${i}].reportId`)}
                </>
              )}
              <LineApprover approver={l.approver} known={known} excludeIds={[meId, f.finalApproverId].filter((x): x is string => !!x)}
                onChange={(p) => setLine(l.id, { approver: p && { id: p.id, fullName: p.fullName } })} />
              {errLine(`modules[${i}].approverId`)}
              <FormInput value={l.note} maxLength={500} onChangeText={(v: string) => setLine(l.id, { note: v })}
                placeholder={t({ ru: 'Примечание (необязательно)', en: 'Note (optional)', az: 'Qeyd (məcburi deyil)' })} />
            </YStack>
          ))}
          {f.modules.length < 50 && (
            <AddLink label={{ ru: 'Добавить модуль', en: 'Add a module', az: 'Modul əlavə et' }}
              onPress={() => up({ modules: [...f.modules, { id: newRowId(), otherName: '', reportId: '', approver: null, note: '' }] })} />
          )}
        </YStack>
        {errLine('modules')}
      </Field>

      <Field label={t({ ru: 'Финальный утверждающий', en: 'Final approver', az: 'Yekun təsdiqləyici' })} required>
        <OptionSelect list options={(o?.finalApprovers ?? []).map((p) => ({ value: p.id, label: p.fullName }))}
          value={f.finalApproverId ? [f.finalApproverId] : []} onChange={(next) => up({ finalApproverId: next[0] })}
          placeholder={t({ ru: 'Выберите утверждающего', en: 'Select the approver', az: 'Təsdiqləyicini seçin' })} highlight={!f.finalApproverId} />
        {errLine('finalApproverId')}
      </Field>

      <Field label={t({ ru: 'Получатели протокола', en: 'Protocol recipients', az: 'Protokolun alıcıları' })}>
        {/* Участники маршрута протокол получат и так — среди получателей их не показываем. */}
        <PeoplePicker
          chosen={f.recipients.filter((p) => !routeIds.includes(p.id))}
          onChange={(next) => up({ recipients: next.map((p) => ({ id: p.id, fullName: p.fullName })) })}
          exclude={routeIds}
          empty={t({ ru: 'Получатели не выбраны.', en: 'No recipients selected.', az: 'Alıcılar seçilməyib.' })}
        />
      </Field>

      <Field label={t({ ru: 'Комментарий', en: 'Comment', az: 'Şərh' })}>
        <FormTextArea value={f.comment} onChangeText={(v: string) => up({ comment: v })} maxLength={2000} minHeight={64}
          placeholder={t({ ru: 'Необязательно', en: 'Optional', az: 'Məcburi deyil' })} />
        {errLine('comment')}
      </Field>

      {saved && <Text fontSize={12} color="$muted">{t({ ru: 'Черновик сохранён', en: 'Draft saved', az: 'Qaralama saxlanıldı' })}</Text>}
      <FormActions
        m={m}
        ok={ok}
        onCancel={props.onCancel ?? onDone}
        onSubmit={submit}
        missing={missing}
        submitLabel={revise
          ? t({ ru: 'Отправить повторно', en: 'Resubmit', az: 'Yenidən göndər' })
          : t({ ru: 'Отправить на согласование', en: 'Submit for approval', az: 'Razılaşdırmaya göndər' })}
        extra={revise ? null : (
          <Button onPress={() => once(() => save.mutate(false))} disabled={save.isPending || uploading > 0}>
            {save.isPending && save.variables === false
              ? t({ ru: 'Сохранение…', en: 'Saving…', az: 'Saxlanılır…' })
              : t({ ru: 'Сохранить черновик', en: 'Save draft', az: 'Qaralamanı saxla' })}
          </Button>
        )}
      />
    </YStack>
  )
}

/** Шапка строки отчёта/модуля: номер и «убрать» — отдельной линией, чтобы на 375 px не переносились. */
function RowHead({ n, removeLabel, onRemove }: { n: number; removeLabel: Localized; onRemove: () => void }) {
  const { t } = useApp()
  return (
    <XStack alignItems="center" justifyContent="space-between">
      <Text fontSize={12} fontWeight="700" color="$primary">{`${n}.`}</Text>
      <IconButton aria-label={t(removeLabel)} onPress={onRemove}>
        <Icon name="x" size={15} color="$muted" />
      </IconButton>
    </XStack>
  )
}

function AddLink({ label, onPress }: { label: Localized; onPress: () => void }) {
  const { t } = useApp()
  return (
    <XStack onPress={onPress} cursor="pointer" alignItems="center" gap={6} role="button">
      <Icon name="plus" size={14} color="$primary" />
      <Text fontSize={12} fontWeight="600" color="$primary">{t(label)}</Text>
    </XStack>
  )
}

/** Согласующий строки. У черновика в payload только id — ФИО берём из известных или дочитываем. */
function LineApprover({ approver, known, excludeIds, onChange }: {
  approver: ItWorksPerson | null
  known: Map<string, string>
  excludeIds: string[]
  onChange: (p: { id: string; fullName: string } | null) => void
}) {
  const name = approver && (approver.fullName || known.get(approver.id))
  const user = useQuery({
    queryKey: ['user', approver?.id],
    queryFn: () => api.user(approver!.id),
    enabled: !!approver && !name,
    staleTime: 60_000,
  })
  return (
    <PersonPickField
      label={{ ru: 'Согласующий', en: 'Approver', az: 'Razılaşdıran' }}
      value={approver && { id: approver.id, fullName: name || user.data?.fullName || '…' }}
      onChange={onChange}
      excludeIds={excludeIds}
    />
  )
}

/**
 * Один файл: выбор → проверка типа и размера → загрузка (edo_doc) → `onChange`. Файл без имени —
 * из черновика (payload хранит только uploadId): показываем «Файл загружен».
 */
function FileSlot({ file, onChange, kind, addLabel, onBusy }: {
  file: ItWorksFile | null
  onChange: (f: ItWorksFile | null) => void
  kind: 'report' | 'invoice'
  addLabel: Localized
  onBusy: (d: number) => void
}) {
  const { t } = useApp()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<Localized | null>(null)
  const name = file ? file.name ?? t(FILE_UPLOADED) : ''

  const pick = async () => {
    const picked = await pickFileWeb(kind === 'invoice' ? '.pdf' : '.pdf,.docx,.xlsx')
    if (!picked) return
    const bad = filePickError(picked, kind)
    setError(bad)
    if (bad) return
    setBusy(true)
    onBusy(1)
    try {
      const form = new FormData()
      form.append('file', picked as unknown as Blob)
      onChange({ uploadId: (await api.uploadEdoDoc(form)).id, name: picked.name, fresh: true })
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
      onBusy(-1)
    }
  }

  if (!isWeb) {
    return <Text fontSize={12} color={file ? '$color' : '$muted'}>{file ? name : t(WEB_ONLY)}</Text>
  }
  const loading = t({ ru: 'Загрузка…', en: 'Uploading…', az: 'Yüklənir…' })
  return (
    <YStack gap={6}>
      {file ? (
        <XStack alignItems="center" gap={8}>
          <Icon name="paperclip" size={14} color="$muted" />
          <Text fontSize={12} color="$color" flex={1} numberOfLines={1}>{name}</Text>
          <Button size="sm" onPress={pick} disabled={busy}>{busy ? loading : t({ ru: 'Заменить', en: 'Replace', az: 'Əvəz et' })}</Button>
          <IconButton aria-label={`${t({ ru: 'Убрать', en: 'Remove', az: 'Sil' })} ${name}`} onPress={() => onChange(null)}>
            <Icon name="x" size={14} color="$muted" />
          </IconButton>
        </XStack>
      ) : (
        <Button block icon={<Icon name="paperclip" size={15} color="$muted" />} onPress={pick} disabled={busy}>
          {busy ? loading : t(addLabel)}
        </Button>
      )}
      {error && <Text fontSize={12} color={tone('red').text}>{t(error)}</Text>}
    </YStack>
  )
}
